import { MutationTree } from 'vuex';
import { IPreviewToken } from '@/document-edit/assembler/document/types/preview-token';
import { EAssemblerMutation } from '@/document-edit/assembler/store/assembler.constants';
import { IAssemblerState } from '@/document-edit/assembler/store/assembler.types';
import { IDocument } from '@lib-shared/types/Document';
import { IDocumentAttachment } from '@lib-shared/types/document-attachment';
import { IDocumentPage } from '@lib-shared/types/document-page';
import { IRecipient } from '@lib-shared/types/Recipient';
import { ITag } from '@lib-shared/types/Tag';
import { IUser } from '@/types/User';
import { IEngagementVideo } from '@lib-shared/types/engagement-video';

export const mutations: MutationTree<IAssemblerState> = {
  [EAssemblerMutation.SetLoading](state: IAssemblerState, loading: boolean) {
    state.loading = loading;
  },
  [EAssemblerMutation.SetLoaded](state: IAssemblerState, loaded: boolean) {
    state.loaded = loaded;
  },
  [EAssemblerMutation.ShowDocumentSettings](state: IAssemblerState, show: boolean) {
    state.showDocumentSettings = show;
  },
  [EAssemblerMutation.SetDocument](state: IAssemblerState, document: IDocument) {
    state.document = document;
  },
  [EAssemblerMutation.SetPages](state: IAssemblerState, pages: IDocumentPage[]) {
    state.document.pages = pages;
  },
  [EAssemblerMutation.ShowEditDocumentPage](
    state: IAssemblerState,
    { isInEditMode, pageId }: { isInEditMode: boolean; pageId: string }
  ) {
    state.document.pages = state.document.pages.map((statePage: IDocumentPage) =>
      statePage.id === pageId
        ? { ...statePage, isInEditMode }
        : { ...statePage, isInEditMode: false }
    );
  },
  [EAssemblerMutation.SetState](state: IAssemblerState, newState: IAssemblerState) {
    Object.assign(state, newState);
  },
  [EAssemblerMutation.SetDocumentTags](state: IAssemblerState, tags: ITag[]) {
    state.document.tags = tags;
  },
  [EAssemblerMutation.SetCurrentDocumentPage](state: IAssemblerState, documentPage: IDocumentPage) {
    state.currentDocumentPage = documentPage;
  },
  [EAssemblerMutation.SetSelectedUser](state: IAssemblerState, user: IUser) {
    state.selectedUser = user;
  },
  [EAssemblerMutation.SetSelectedRecipient](
    state: IAssemblerState,
    selected: { id: string; connectFirstRecipient: boolean }
  ) {
    state.selectedRecipient = selected;
  },
  [EAssemblerMutation.AddRecipient](state: IAssemblerState, recipient: IRecipient) {
    state.document.recipients = [...state.document.recipients, recipient];
  },
  [EAssemblerMutation.UpdateRecipient](state: IAssemblerState, updatedRecipient: IRecipient) {
    state.document.recipients =
      !!state.document.recipients.length &&
      !!state.document.recipients.some(
        (stateRecipient: IRecipient) => stateRecipient.id === updatedRecipient.id
      )
        ? state.document.recipients.map((stateRecipient: IRecipient) =>
            stateRecipient.id === updatedRecipient.id ? updatedRecipient : stateRecipient
          )
        : [...state.document.recipients, updatedRecipient];
  },
  [EAssemblerMutation.UpdateDocumentAttachment](
    state: IAssemblerState,
    documentAttachments: IDocumentAttachment[]
  ) {
    state.document.documentAttachments = documentAttachments;
  },
  [EAssemblerMutation.SetRecipients](state: IAssemblerState, recipients: IRecipient[]) {
    state.document.recipients = recipients;
  },
  [EAssemblerMutation.UpdateDocumentUser](state: IAssemblerState, user: IUser) {
    state.document.user = user;
  },
  [EAssemblerMutation.SetPreviewToken](state: IAssemblerState, previewToken: IPreviewToken) {
    state.previewToken = previewToken;
  },
  [EAssemblerMutation.SetCreateDocumentId](state: IAssemblerState, documentId: string) {
    state.createDocumentId = documentId;
  },
  [EAssemblerMutation.SetSealValidationSuccess](
    state: IAssemblerState,
    sealValidationSuccess: boolean
  ) {
    state.sealValidationSuccess = sealValidationSuccess;
  },
  [EAssemblerMutation.SetSidebarCollapsed](state: IAssemblerState, sidebarCollapsed: boolean) {
    state.sidebarCollapsed = sidebarCollapsed;
  },
  [EAssemblerMutation.SetEngageVideo](state: IAssemblerState, video: IEngagementVideo) {
    state.document.video = video;
  },
  [EAssemblerMutation.SetActiveContentSelector](
    state: IAssemblerState,
    activeContentSelector: number
  ) {
    state.activeContentSelector = activeContentSelector;
  },
  [EAssemblerMutation.ShowAddRoleModal](state: IAssemblerState, show: boolean) {
    state.showAddRoleModal = show;
  },
  [EAssemblerMutation.SetPageToBeReplaced](
    state: IAssemblerState,
    pageToBeReplaced: IDocumentPage
  ) {
    state.pageToBeReplaced = pageToBeReplaced;
  },
};
